/**
 * Academic — the rules a programme runs by.
 *
 * Phase 1: faculties' programmes and their configurable rules, modules with
 * their assessment structure, the lecturer register, lecturer-to-module
 * allocation, the academic calendar, and pending rule changes awaiting a
 * super admin.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { schoolApi } from './schoolApi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Loader2, GraduationCap, BookOpen, Users, CalendarDays, ShieldAlert, Plus, Trash2 } from 'lucide-react';

const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));

export default function SchoolAcademic({ tenantId }) {
  const [tab, setTab] = useState('programmes');
  const { data: cat } = useQuery({ queryKey: ['academic-catalogue'], queryFn: () => schoolApi('/academic/catalogue') });
  const base = `/tenants/${tenantId}/academic`;

  return (
    <div className="space-y-4" data-testid="school-academic">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">Academic</h2>
        <p className="text-sm text-slate-500">
          Every programme carries its own rules — entry, modules, pass marks, attendance, practicals and award.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="academic-tabs">
          <TabsTrigger value="programmes" data-testid="academic-tab-programmes">Programmes</TabsTrigger>
          <TabsTrigger value="modules" data-testid="academic-tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="lecturers" data-testid="academic-tab-lecturers">Lecturers</TabsTrigger>
          <TabsTrigger value="allocations" data-testid="academic-tab-allocations">Allocations</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="academic-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="academic-tab-approvals">Approvals</TabsTrigger>
        </TabsList>
        <TabsContent value="programmes"><Programmes base={base} cat={cat} /></TabsContent>
        <TabsContent value="modules"><Modules base={base} cat={cat} /></TabsContent>
        <TabsContent value="lecturers"><Lecturers base={base} /></TabsContent>
        <TabsContent value="allocations"><Allocations base={base} /></TabsContent>
        <TabsContent value="calendar"><Calendar base={base} /></TabsContent>
        <TabsContent value="approvals"><Approvals base={base} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Loading = () => <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

// ---------------------------------------------------------------- programmes

function Programmes({ base, cat }) {
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['academic-programmes', base], queryFn: () => schoolApi(`${base}/programmes`) });
  if (isLoading) return <Loading />;
  const items = data?.items || [];
  return (
    <div className="space-y-3">
      {items.map(p => (
        <Card key={p.id} data-testid={`programme-row-${p.id}`}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-indigo-500" />{p.code} · {p.name}
                  {p.ownership === 'regulated'
                    ? <Badge variant="outline" className="text-[10px]">{p.awarding_body || 'externally regulated'}</Badge>
                    : <Badge variant="secondary" className="text-[10px]">institutional</Badge>}
                  {p.pending_rule_changes > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-800">{p.pending_rule_changes} awaiting approval</Badge>}
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {p.award_type || p.award || '—'} · {p.nqf_level ? `Level ${p.nqf_level}` : 'no level set'} ·{' '}
                  {p.duration_years || '?'} yrs · {p.module_count} modules · pass {p.rules?.minimum_pass_mark}% ·
                  attendance {p.rules?.attendance_requirement}%
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`programme-configure-${p.id}`}>Configure rules</Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 flex flex-wrap gap-1">
            {(p.delivery_modes || []).map(m => <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{m.replace(/_/g, ' ')}</span>)}
            {p.rules?.ojet_required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">OJET {p.rules.ojet_weeks || ''} wks</span>}
            {p.rules?.research_required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">research</span>}
            {p.rules?.practical_required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">practical</span>}
          </CardContent>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-500">No programmes yet.</p>}
      {editing && <ProgrammeDialog base={base} cat={cat} programme={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ProgrammeDialog({ base, cat, programme, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    award_type: programme.award_type || programme.award || '',
    nqf_level: programme.nqf_level || '',
    duration_years: programme.duration_years || '',
    ownership: programme.ownership || 'institutional',
    awarding_body: programme.awarding_body || '',
    delivery_modes: programme.delivery_modes || [],
    change_note: '',
  });
  const [rules, setRules] = useState(programme.rules || {});
  const set = (k, v) => setRules(r => ({ ...r, [k]: v }));
  const save = useMutation({
    mutationFn: () => schoolApi(`${base}/programmes/${programme.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...form,
        nqf_level: form.nqf_level === '' ? null : num(form.nqf_level),
        duration_years: form.duration_years === '' ? null : num(form.duration_years),
        rules: {
          ...rules,
          entry_requirements: String(rules.entry_requirements || '').length
            ? (Array.isArray(rules.entry_requirements) ? rules.entry_requirements : String(rules.entry_requirements).split('\n').filter(Boolean))
            : [],
          conditions_of_award: String(rules.conditions_of_award || '').length
            ? (Array.isArray(rules.conditions_of_award) ? rules.conditions_of_award : String(rules.conditions_of_award).split('\n').filter(Boolean))
            : [],
        },
      }),
    }),
    onSuccess: (res) => { toast.success(res.message || 'Saved'); qc.invalidateQueries({ queryKey: ['academic-programmes', base] }); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const modes = cat?.delivery_modes || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="programme-dialog">
        <DialogHeader>
          <DialogTitle>{programme.code} · {programme.name}</DialogTitle>
          <DialogDescription>
            {form.ownership === 'regulated'
              ? 'Externally regulated: rule changes are saved as a request for a super admin to approve.'
              : 'Institutionally owned: your rules apply as soon as you save.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-2">
            <div><Label>Award type</Label><Input value={form.award_type} onChange={e => setForm({ ...form, award_type: e.target.value })} placeholder="Diploma" data-testid="prog-award" /></div>
            <div><Label>NQF / level</Label><Input type="number" value={form.nqf_level} onChange={e => setForm({ ...form, nqf_level: e.target.value })} data-testid="prog-level" /></div>
            <div><Label>Duration (years)</Label><Input type="number" step="0.5" value={form.duration_years} onChange={e => setForm({ ...form, duration_years: e.target.value })} data-testid="prog-duration" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><Label>Ownership</Label>
              <Select value={form.ownership} onValueChange={v => setForm({ ...form, ownership: v })}>
                <SelectTrigger data-testid="prog-ownership"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="institutional">Institutionally owned</SelectItem>
                  <SelectItem value="regulated">Externally regulated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.ownership === 'regulated' && (
              <div><Label>Awarding body</Label><Input value={form.awarding_body} onChange={e => setForm({ ...form, awarding_body: e.target.value })} placeholder="HEXCO" data-testid="prog-awarding-body" /></div>
            )}
          </div>
          <div>
            <Label>Modes of delivery</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {modes.map(m => (
                <button key={m} type="button" data-testid={`prog-mode-${m}`}
                        onClick={() => setForm({ ...form, delivery_modes: form.delivery_modes.includes(m) ? form.delivery_modes.filter(x => x !== m) : [...form.delivery_modes, m] })}
                        className={`text-xs px-2.5 py-1 rounded-full border ${form.delivery_modes.includes(m) ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600'}`}>
                  {m.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700">Programme rules</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">Modules required</Label><Input type="number" value={rules.total_modules_required ?? ''} onChange={e => set('total_modules_required', num(e.target.value))} data-testid="rule-modules" /></div>
              <div><Label className="text-xs">Credits required</Label><Input type="number" value={rules.total_credits_required ?? ''} onChange={e => set('total_credits_required', num(e.target.value))} data-testid="rule-credits" /></div>
              <div><Label className="text-xs">Minimum pass mark %</Label><Input type="number" value={rules.minimum_pass_mark ?? ''} onChange={e => set('minimum_pass_mark', num(e.target.value))} data-testid="rule-pass-mark" /></div>
              <div><Label className="text-xs">Attendance requirement %</Label><Input type="number" value={rules.attendance_requirement ?? ''} onChange={e => set('attendance_requirement', num(e.target.value))} data-testid="rule-attendance" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-xs"><Switch checked={!!rules.practical_required} onCheckedChange={v => set('practical_required', v)} data-testid="rule-practical" /> Practical</label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={!!rules.ojet_required} onCheckedChange={v => set('ojet_required', v)} data-testid="rule-ojet" /> OJET</label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={!!rules.research_required} onCheckedChange={v => set('research_required', v)} data-testid="rule-research" /> Research</label>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {rules.practical_required && <div><Label className="text-xs">Practical hours</Label><Input type="number" value={rules.practical_hours ?? ''} onChange={e => set('practical_hours', num(e.target.value))} /></div>}
              {rules.ojet_required && <div><Label className="text-xs">OJET weeks</Label><Input type="number" value={rules.ojet_weeks ?? ''} onChange={e => set('ojet_weeks', num(e.target.value))} data-testid="rule-ojet-weeks" /></div>}
              {rules.research_required && <div><Label className="text-xs">Research credits</Label><Input type="number" value={rules.research_credits ?? ''} onChange={e => set('research_credits', num(e.target.value))} /></div>}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">Entry requirements (one per line)</Label>
                <textarea className="w-full text-xs border rounded-md p-2 h-20" data-testid="rule-entry"
                          value={Array.isArray(rules.entry_requirements) ? rules.entry_requirements.join('\n') : (rules.entry_requirements || '')}
                          onChange={e => set('entry_requirements', e.target.value)} />
              </div>
              <div><Label className="text-xs">Conditions of award (one per line)</Label>
                <textarea className="w-full text-xs border rounded-md p-2 h-20" data-testid="rule-award"
                          value={Array.isArray(rules.conditions_of_award) ? rules.conditions_of_award.join('\n') : (rules.conditions_of_award || '')}
                          onChange={e => set('conditions_of_award', e.target.value)} />
              </div>
            </div>
            {form.ownership === 'regulated' && (
              <div><Label className="text-xs">Note for the approver</Label><Input value={form.change_note} onChange={e => setForm({ ...form, change_note: e.target.value })} data-testid="rule-note" /></div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="programme-save">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------- modules

function Modules({ base, cat }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['academic-modules', base], queryFn: () => schoolApi(`${base}/modules`) });
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-3">
      {(data?.items || []).map(m => (
        <Card key={m.id} data-testid={`module-row-${m.id}`}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2"><BookOpen className="h-4 w-4 text-teal-600" />{m.code} · {m.name}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {m.module_type} · {m.credits ?? 0} credits · {m.notional_hours ?? 0} notional hrs ·{' '}
                  {m.assessment?.length ? `${m.assessment.length} assessment parts (${m.assessment_total}%)` : 'no assessment set'} ·{' '}
                  {m.programme_ids?.length || 0} programme(s)
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(m)} data-testid={`module-configure-${m.id}`}>Configure</Button>
            </div>
          </CardHeader>
        </Card>
      ))}
      {editing && <ModuleDialog base={base} cat={cat} module={editing}
                                onClose={() => { qc.invalidateQueries({ queryKey: ['academic-modules', base] }); setEditing(null); }} />}
    </div>
  );
}

function ModuleDialog({ base, cat, module, onClose }) {
  const [form, setForm] = useState({
    notional_hours: module.notional_hours || '', credits: module.credits || '',
    level: module.level || '', module_type: module.module_type || 'core',
  });
  const [parts, setParts] = useState(module.assessment?.length ? module.assessment : []);
  const total = parts.reduce((s, p) => s + Number(p.weight || 0), 0);
  const { data: progs } = useQuery({ queryKey: ['academic-programmes', base], queryFn: () => schoolApi(`${base}/programmes`) });
  const [shared, setShared] = useState(module.programme_ids || []);

  const save = useMutation({
    mutationFn: async () => {
      await schoolApi(`${base}/modules/${module.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...form, notional_hours: form.notional_hours === '' ? null : num(form.notional_hours), credits: form.credits === '' ? null : num(form.credits), assessment: parts }),
      });
      return schoolApi(`${base}/modules/${module.id}/programmes`, { method: 'POST', body: JSON.stringify({ programme_ids: shared }) });
    },
    onSuccess: () => { toast.success('Module saved'); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="module-dialog">
        <DialogHeader>
          <DialogTitle>{module.code} · {module.name}</DialogTitle>
          <DialogDescription>Weightings must total 100 before this can be saved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-4 gap-2">
            <div><Label className="text-xs">Notional hours</Label><Input type="number" value={form.notional_hours} onChange={e => setForm({ ...form, notional_hours: e.target.value })} data-testid="module-hours" /></div>
            <div><Label className="text-xs">Credits</Label><Input type="number" value={form.credits} onChange={e => setForm({ ...form, credits: e.target.value })} data-testid="module-credits" /></div>
            <div><Label className="text-xs">Level</Label><Input value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} data-testid="module-level" /></div>
            <div><Label className="text-xs">Type</Label>
              <Select value={form.module_type} onValueChange={v => setForm({ ...form, module_type: v })}>
                <SelectTrigger data-testid="module-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(cat?.module_types || []).map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Assessment structure <span className={total === 100 ? 'text-emerald-600' : 'text-amber-600'}>({total}%)</span></p>
              <Button variant="ghost" size="sm" data-testid="assessment-add"
                      onClick={() => setParts([...parts, { type: 'test', count: 1, weight: 0 }])}>
                <Plus className="h-3.5 w-3.5 mr-1" />Add part
              </Button>
            </div>
            {parts.map((p, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-end" data-testid={`assessment-part-${i}`}>
                <div><Label className="text-[10px]">Type</Label>
                  <Select value={p.type} onValueChange={v => setParts(parts.map((x, j) => j === i ? { ...x, type: v } : x))}>
                    <SelectTrigger data-testid={`assessment-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(cat?.assessment_types || []).map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px]">How many</Label><Input type="number" min={1} value={p.count} onChange={e => setParts(parts.map((x, j) => j === i ? { ...x, count: num(e.target.value) } : x))} data-testid={`assessment-count-${i}`} /></div>
                <div><Label className="text-[10px]">Weight %</Label><Input type="number" value={p.weight} onChange={e => setParts(parts.map((x, j) => j === i ? { ...x, weight: num(e.target.value) } : x))} data-testid={`assessment-weight-${i}`} /></div>
                <Button variant="ghost" size="sm" onClick={() => setParts(parts.filter((_, j) => j !== i))} data-testid={`assessment-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs">Also serves these programmes</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {(progs?.items || []).map(p => (
                <button key={p.id} type="button" data-testid={`module-programme-${p.id}`}
                        onClick={() => setShared(shared.includes(p.id) ? shared.filter(x => x !== p.id) : [...shared, p.id])}
                        className={`text-xs px-2.5 py-1 rounded-full border ${shared.includes(p.id) ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`}>
                  {p.code}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="module-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------- lecturers

function Lecturers({ base }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['academic-lecturers', base], queryFn: () => schoolApi(`${base}/lecturers`) });
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-2">
      {(data?.items || []).map(l => (
        <Card key={l.id} data-testid={`lecturer-row-${l.id}`}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-indigo-500" />{l.full_name || l.email}
                  <Badge variant={l.status === 'active' ? 'secondary' : 'outline'} className="text-[10px]">{l.status}</Badge>
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {(l.qualifications || []).join(', ') || 'no qualifications recorded'} ·{' '}
                  {l.authorised_module_ids.length} module(s) authorised · {l.cpd_count} CPD · {l.allocation_count} allocation(s)
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(l)} data-testid={`lecturer-configure-${l.id}`}>Register details</Button>
            </div>
          </CardHeader>
        </Card>
      ))}
      {editing && <LecturerDialog base={base} lecturer={editing}
                                  onClose={() => { qc.invalidateQueries({ queryKey: ['academic-lecturers', base] }); setEditing(null); }} />}
    </div>
  );
}

function LecturerDialog({ base, lecturer, onClose }) {
  const [form, setForm] = useState({
    faculty_id: lecturer.faculty_id || '',
    qualifications: (lecturer.qualifications || []).join('\n'),
    teaching_qualification: lecturer.teaching_qualification || '',
    professional_experience: lecturer.professional_experience || '',
    authorised_levels: (lecturer.authorised_levels || []).join(', '),
    appointment_date: lecturer.appointment_date || '',
    status: lecturer.status || 'active',
    performance_review_date: lecturer.performance_review_date || '',
  });
  const [mods, setMods] = useState(lecturer.authorised_module_ids || []);
  const [cpd, setCpd] = useState({ title: '', provider: '', completed_on: '', hours: '' });
  const { data: modules } = useQuery({ queryKey: ['academic-modules', base], queryFn: () => schoolApi(`${base}/modules`) });
  const { data: cpdRows, refetch } = useQuery({ queryKey: ['lecturer-cpd', lecturer.id], queryFn: () => schoolApi(`${base}/lecturers/${lecturer.id}/cpd`) });

  const save = useMutation({
    mutationFn: () => schoolApi(`${base}/lecturers/${lecturer.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...form,
        qualifications: form.qualifications.split('\n').filter(Boolean),
        authorised_levels: form.authorised_levels.split(',').map(s => s.trim()).filter(Boolean),
        authorised_module_ids: mods,
        documents: lecturer.documents || [],
      }),
    }),
    onSuccess: () => { toast.success('Register updated'); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const addCpd = useMutation({
    mutationFn: () => schoolApi(`${base}/lecturers/${lecturer.id}/cpd`, { method: 'POST', body: JSON.stringify({ ...cpd, hours: num(cpd.hours) }) }),
    onSuccess: () => { toast.success('CPD recorded'); setCpd({ title: '', provider: '', completed_on: '', hours: '' }); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="lecturer-dialog">
        <DialogHeader>
          <DialogTitle>{lecturer.full_name || lecturer.email}</DialogTitle>
          <DialogDescription>Only what is on the register may be allocated to a module.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <div><Label className="text-xs">Teaching qualification</Label><Input value={form.teaching_qualification} onChange={e => setForm({ ...form, teaching_qualification: e.target.value })} data-testid="lecturer-teaching-qual" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="lecturer-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="probation">On probation</SelectItem>
                  <SelectItem value="inactive">Deactivated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Appointed on</Label><Input type="date" value={form.appointment_date} onChange={e => setForm({ ...form, appointment_date: e.target.value })} data-testid="lecturer-appointed" /></div>
            <div><Label className="text-xs">Next performance review</Label><Input type="date" value={form.performance_review_date} onChange={e => setForm({ ...form, performance_review_date: e.target.value })} data-testid="lecturer-review" /></div>
          </div>
          <div><Label className="text-xs">Qualifications (one per line)</Label>
            <textarea className="w-full text-xs border rounded-md p-2 h-16" value={form.qualifications} onChange={e => setForm({ ...form, qualifications: e.target.value })} data-testid="lecturer-quals" />
          </div>
          <div><Label className="text-xs">Professional / ministry experience</Label>
            <textarea className="w-full text-xs border rounded-md p-2 h-16" value={form.professional_experience} onChange={e => setForm({ ...form, professional_experience: e.target.value })} data-testid="lecturer-experience" />
          </div>
          <div><Label className="text-xs">Levels authorised (comma separated)</Label><Input value={form.authorised_levels} onChange={e => setForm({ ...form, authorised_levels: e.target.value })} placeholder="4, 5" data-testid="lecturer-levels" /></div>
          <div>
            <Label className="text-xs">Modules authorised to teach</Label>
            <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
              {(modules?.items || []).map(m => (
                <button key={m.id} type="button" data-testid={`lecturer-module-${m.id}`}
                        onClick={() => setMods(mods.includes(m.id) ? mods.filter(x => x !== m.id) : [...mods, m.id])}
                        className={`text-xs px-2.5 py-1 rounded-full border ${mods.includes(m.id) ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600'}`}>
                  {m.code}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-slate-700">CPD record</p>
            {(cpdRows?.items || []).map(c => (
              <p key={c.id} className="text-xs text-slate-600" data-testid={`cpd-row-${c.id}`}>{c.completed_on || '—'} · {c.title} {c.hours ? `(${c.hours}h)` : ''}</p>
            ))}
            <div className="grid grid-cols-4 gap-2 items-end">
              <Input placeholder="Title" value={cpd.title} onChange={e => setCpd({ ...cpd, title: e.target.value })} data-testid="cpd-title" />
              <Input placeholder="Provider" value={cpd.provider} onChange={e => setCpd({ ...cpd, provider: e.target.value })} data-testid="cpd-provider" />
              <Input type="date" value={cpd.completed_on} onChange={e => setCpd({ ...cpd, completed_on: e.target.value })} data-testid="cpd-date" />
              <Button size="sm" variant="outline" onClick={() => addCpd.mutate()} disabled={!cpd.title} data-testid="cpd-add">Add</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="lecturer-save">Save register</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------- allocations

function Allocations({ base }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ lecturer_id: '', module_id: '', academic_year_id: '', term_id: '' });
  const { data: rows, isLoading } = useQuery({ queryKey: ['academic-allocations', base], queryFn: () => schoolApi(`${base}/allocations`) });
  const { data: lecturers } = useQuery({ queryKey: ['academic-lecturers', base], queryFn: () => schoolApi(`${base}/lecturers`) });
  const { data: modules } = useQuery({ queryKey: ['academic-modules', base], queryFn: () => schoolApi(`${base}/modules`) });
  const { data: calendar } = useQuery({ queryKey: ['academic-calendar', base], queryFn: () => schoolApi(`${base}/calendar`) });
  const semesters = useMemo(() => (calendar?.items || []).find(y => y.id === form.academic_year_id)?.semesters || [], [calendar, form.academic_year_id]);

  const allocate = useMutation({
    mutationFn: () => schoolApi(`${base}/allocations`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Lecturer allocated'); qc.invalidateQueries({ queryKey: ['academic-allocations', base] }); },
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id) => schoolApi(`${base}/allocations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['academic-allocations', base] }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Allocate a lecturer to a module</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-5 gap-2 items-end">
          <div><Label className="text-xs">Lecturer</Label>
            <Select value={form.lecturer_id} onValueChange={v => setForm({ ...form, lecturer_id: v })}>
              <SelectTrigger data-testid="alloc-lecturer"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{(lecturers?.items || []).map(l => <SelectItem key={l.id} value={l.id}>{l.full_name || l.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Module</Label>
            <Select value={form.module_id} onValueChange={v => setForm({ ...form, module_id: v })}>
              <SelectTrigger data-testid="alloc-module"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{(modules?.items || []).map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Academic year</Label>
            <Select value={form.academic_year_id} onValueChange={v => setForm({ ...form, academic_year_id: v, term_id: '' })}>
              <SelectTrigger data-testid="alloc-year"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{(calendar?.items || []).map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Semester</Label>
            <Select value={form.term_id} onValueChange={v => setForm({ ...form, term_id: v })}>
              <SelectTrigger data-testid="alloc-semester"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{semesters.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={() => allocate.mutate()} disabled={!form.lecturer_id || !form.module_id || !form.academic_year_id} data-testid="alloc-save">Allocate</Button>
        </CardContent>
      </Card>
      {isLoading ? <Loading /> : (
        <div className="space-y-2">
          {(rows?.items || []).map(a => (
            <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`allocation-row-${a.id}`}>
              <p className="text-xs text-slate-600">{a.lecturer_name} · {a.module_code} {a.level ? `· level ${a.level}` : ''}</p>
              <Button variant="ghost" size="sm" onClick={() => remove.mutate(a.id)} data-testid={`allocation-remove-${a.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          ))}
          {(rows?.items || []).length === 0 && <p className="text-sm text-slate-500">No allocations yet.</p>}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ calendar

function Calendar({ base }) {
  const { data, isLoading } = useQuery({ queryKey: ['academic-calendar', base], queryFn: () => schoolApi(`${base}/calendar`) });
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-3" data-testid="academic-calendar">
      {(data?.items || []).map(y => (
        <Card key={y.id} data-testid={`calendar-year-${y.id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-500" />{y.name}
              {y.is_current && <Badge variant="secondary" className="text-[10px]">current</Badge>}
            </CardTitle>
            <p className="text-xs text-slate-500">{y.start_date} → {y.end_date}</p>
          </CardHeader>
          <CardContent className="pt-0 space-y-1">
            {(y.semesters || []).map(s => (
              <p key={s.id} className="text-xs text-slate-600" data-testid={`calendar-semester-${s.id}`}>
                Semester {s.semester_no || '?'} · {s.name} · {s.start_date} → {s.end_date}
                {s.results_locked && <span className="text-amber-600"> · results locked</span>}
              </p>
            ))}
            {(y.semesters || []).length === 0 && <p className="text-xs text-slate-400">No semesters configured for this year.</p>}
          </CardContent>
        </Card>
      ))}
      {(data?.items || []).length === 0 && <p className="text-sm text-slate-500">No academic years configured yet.</p>}
    </div>
  );
}

// ----------------------------------------------------------------- approvals

function Approvals({ base }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['academic-rule-changes', base], queryFn: () => schoolApi(`${base}/rule-changes?status=pending`) });
  const decide = useMutation({
    mutationFn: ({ id, decision }) => schoolApi(`${base}/rule-changes/${id}/${decision}`, { method: 'POST' }),
    onSuccess: (_r, v) => { toast.success(v.decision === 'approve' ? 'Rules applied' : 'Change rejected'); qc.invalidateQueries({ queryKey: ['academic-rule-changes', base] }); qc.invalidateQueries({ queryKey: ['academic-programmes', base] }); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <Loading />;
  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="academic-approvals">
      {items.map(c => (
        <Card key={c.id} data-testid={`rule-change-${c.id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" />{c.programme_name}</CardTitle>
            <p className="text-xs text-slate-500">Requested by {c.requested_by} · pass {c.rules?.minimum_pass_mark}% · attendance {c.rules?.attendance_requirement}%{c.note ? ` · "${c.note}"` : ''}</p>
          </CardHeader>
          <CardContent className="pt-0 flex gap-2">
            <Button size="sm" onClick={() => decide.mutate({ id: c.id, decision: 'approve' })} data-testid={`rule-approve-${c.id}`}>Approve</Button>
            <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: c.id, decision: 'reject' })} data-testid={`rule-reject-${c.id}`}>Reject</Button>
          </CardContent>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-slate-500">Nothing waiting for approval.</p>}
    </div>
  );
}
